import { ensureRouteData } from '@/app/query-client'
import { GAINERS_LOSERS_WEEK_AVAILABLE } from '@/features/prices/api/pricesService'
import { gainersLosersQueryOptions } from '@/features/prices/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { gainersLosersQuerySchema } from '@/schemas/prices'

/**
 * `period`, `page` and `pageSize` live in the URL: the "1 day / 1 week" toggle and the one pager
 * under both columns. Moved here from the screen file, as `stockSearchSchema` moved into
 * `StockScreen.loader.ts`: a loader may not import a component module, so a schema the loader
 * also parses has to live somewhere both it and the route can reach. Re-imported by the screen
 * for `validateSearch`, so the two can never disagree about what the URL means.
 */
export const gainersLosersSearchSchema = gainersLosersQuerySchema

/**
 * The one parse both the loader and the screen read `search` through — see
 * `toMoversQuery` for why the screen may not key off the raw `Route.useSearch()`.
 */
export const toGainersLosersQuery = (search: unknown) => gainersLosersSearchSchema.parse(search)

/**
 * The `/market/gainers-losers` loader.
 *
 * MAIN, keyed on the whole query — awaited for the entry URL only (no `loaderDeps`), so the
 * toggle and the pager re-run no loader and the page dims in place (`keepPreviousData`). A week
 * with no feed yet (`GAINERS_LOSERS_WEEK_AVAILABLE`) loads nothing: the page draws its no-data
 * state for it, and there is nothing to wait on.
 *
 * No secondary reads: both columns come off this one query.
 */
export async function loadGainersLosers({
  context: { queryClient },
  location,
}: {
  context: RouterContext
  location: { search: unknown }
}): Promise<void> {
  const query = toGainersLosersQuery(location.search)
  if (query.period === '1w' && !GAINERS_LOSERS_WEEK_AVAILABLE) return
  await ensureRouteData(queryClient, gainersLosersQueryOptions(query))
}
